import { resetUserChanges, updateUser } from "@/redux/feature/user/userSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { X } from "lucide-react";
import { SelectTopics } from "@/pages/authentication/signUp/components/ProfessionalDetails";
import FileUploadFieldPlus from "@/components/ui/FileUploadFieldPlus";
import { FileDetails, TUser } from "@/types/types";
import { CustomButton } from "@/components/ui/Buttons";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import { useUpdateCreatorMutation } from "@/redux/feature/user/userApi";
import SelectSkills from "@/components/ui/SelectSkills";
import MultiLinkField from "@/pages/authentication/signUp/components/MultiLinkField";

// ✅ Exact same V2 components used across ApplicationStep1–6
import AuthInput from "@/pages/authenticationV2/signUp/components/AuthInput";
import AuthDropdown, {
  AuthDropdownOption,
} from "@/pages/authenticationV2/signUp/components/AuthDropdown";
import { experienceLevel } from "@/constant";
import {
  LanguageItemV2,
  LanguagesWithFluencySection,
} from "./EditCreatorExtrasModal";

interface EditProfessionalDetailsModalProps {
  data: TUser;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const experienceLevelOptions: AuthDropdownOption[] = experienceLevel.map(
  (e) => ({ key: e.id, label: e.title }),
);

const availableHoursOptions = [
  "1–5 hrs / week",
  "6–10 hrs / week",
  "11–20 hrs / week",
  "20+ hrs / week",
];

/* ── Section divider — exact same orange pill style as step headers ── */
const SectionDivider = ({ title }: { title: string }) => (
  <div className="flex items-center gap-x-2">
    <div className="bg-[#FF6600] px-[8.5px] rounded-[20px] shrink-0">
      <p className="text-white text-[11px] leading-5 tracking-normal">
        {title}
      </p>
    </div>
    <div className="flex-1 h-px bg-[#F2F4F7]" />
  </div>
);

const EditProfessionalDetailsModal = ({
  data,
  onClose,
}: EditProfessionalDetailsModalProps) => {
  const dispatch = useAppDispatch();
  const userSliceData = useAppSelector((state) => state.user.user);
  const [updateCreator] = useUpdateCreatorMutation();

  const selectedExperience =
    experienceLevelOptions.find((o) => o.key === userSliceData?.experience) ??
    null;

  const handlerUpdateCreator = async () => {
    if (!data || !data.user_id) {
      toast.error("User information is missing");
      return;
    }

    const payload = {
      id: data.user_id,
      body: {
        videoLinks: userSliceData.videoLinks || [],
        language: userSliceData.language || "",
        socialMedia: userSliceData.socialMedia || "",
        experience: userSliceData.experience || "",
        topics: userSliceData.topics || "",
        skills: userSliceData.skills || "",
        hoursPerWeek: userSliceData.hoursPerWeek || "",
        availableInWeekends: userSliceData.availableInWeekends || false,
        brandsDontWantToWorkWith: userSliceData.brandsDontWantToWorkWith || "",
        salary: userSliceData.salary || "",
        payPerVideo: userSliceData.payPerVideo || "",
        wardrobeStyles: userSliceData.wardrobeStyles || [],
        availableBackgrounds: userSliceData.availableBackgrounds || [],
      },
    };

    await handleApiRequest({
      payload,
      requestFunction: updateCreator,
      onSuccess: () => {
        toast.success("Professional details updated successfully!");
        onClose(false);
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };
        if (error?.data?.errorSource?.length) {
          error.data.errorSource.forEach((item) => {
            toast.error(
              item.path ? `${item.path} is ${item.message}` : item.message,
            );
          });
        } else {
          toast.error(
            "Failed to update professional details. Please try again.",
          );
        }
      },
    });
  };

  const handlerCancel = () => {
    dispatch(resetUserChanges(data));
    onClose(false);
  };

  return (
    <section className="bg-white rounded-2xl flex flex-col max-h-[90vh]">
      {/* ── Header — same orange pill + h1 pattern as every step ── */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-[#F2F4F7] shrink-0">
        <div className="flex items-center gap-x-2">
          <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
            <p className="text-white text-[11px] leading-5 tracking-normal">
              Edit
            </p>
          </div>
          <h1 className="text-[#060B13] text-xl tracking-[-0.7px] font-medium">
            Professional Details
          </h1>
        </div>
        <button
          onClick={handlerCancel}
          className="text-[#98A2B3] hover:text-[#060B13] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
        {/* ── Profile & Experience ── */}
        {/* <SectionDivider title="Profile & Experience" /> */}

        {/* AuthInput — same as Step3 address, mapLink fields */}
        {/* <AuthInput
          label="LinkedIn / Instagram Profile Link"
          name="socialMedia"
          placeholder="https://instagram.com/yourhandle"
          value={userSliceData.socialMedia}
          onChange={(e) =>
            dispatch(
              updateUser({ field: "socialMedia", value: e.target.value }),
            )
          }
        /> */}

        {/* AuthDropdown — same as Step1 gender, Step3 country/state/city */}
        {/* <AuthDropdown
          label="Experience Level"
          options={experienceLevelOptions}
          value={selectedExperience}
          onChange={(opt) =>
            dispatch(
              updateUser({ field: "experience", value: String(opt.key) }),
            )
          }
        /> */}

        {/* Video links */}
        {/* <div>
          <p className="text-[#121217] text-sm font-medium tracking-[-0.3px] mb-1">
            Previous Video Links
          </p>
          <p className="text-[#475467] text-xs tracking-[-0.5%] mt-[2px] mb-3">
            Add links to videos with you speaking in them (UGC preferred).
          </p>
          <MultiLinkField
            hideRemoveFileButton={true}
            links={data.videoLinks}
            onLinksChange={(updatedLinks) =>
              dispatch(updateUser({ field: "videoLinks", value: updatedLinks }))
            }
          />
        </div> */}

        {/* ── Skills & Topics ── */}
        <SectionDivider title="Skills & Topics" />

        <div>
          <SelectSkills
            setValue={(value) =>
              dispatch(updateUser({ field: "skills", value }))
            }
            defaultValue={userSliceData.skills}
          />
        </div>

        <div>
          <SelectTopics
            setValue={(value) =>
              dispatch(updateUser({ field: "topics", value }))
            }
            defaultValue={userSliceData.topics}
          />
        </div>

        {/* ── Availability ── */}
        <SectionDivider title="Availability" />

        {/* Weekly hours — exact same chip buttons + bg-[#FAFAFA] card as Step4 */}
        <div>
          <p className="tracking-normal text-sm text-[#121217] font-medium">
            Availability
          </p>
          <p className="mt-[2px] text-[10px] tracking-normal text-[#475467]">
            This helps us prioritise you for urgent or time-sensitive projects.
          </p>

          <div className="bg-[#FAFAFA] py-3 px-2 rounded-lg mt-2">
            <p className="text-[#121217] text-xs tracking-normal">
              Weekly availability
            </p>

            <div className="flex flex-wrap mt-2 gap-2">
              {availableHoursOptions.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() =>
                    dispatch(
                      updateUser({ field: "hoursPerWeek", value: label }),
                    )
                  }
                  className={`px-1.5 py-1 border rounded-lg bg-white text-xs tracking-[-0.5px]
                    ${
                      userSliceData?.hoursPerWeek === label
                        ? "border-[#FF6600] bg-[#FFF4EC]"
                        : "border-[#D8D8DD]"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Checkbox — exact same as Step4 flexible timings */}
            <div className="mt-6">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={userSliceData?.availableInWeekends === true}
                  onChange={(e) =>
                    dispatch(
                      updateUser({
                        field: "availableInWeekends",
                        value: e.target.checked,
                      }),
                    )
                  }
                  className="size-3.5 rounded border border-[#D0D5DD] accent-[#F97316]"
                />
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="font-medium text-[#121217]">
                    Flexible timings
                  </span>
                  <span className="text-[#475467]">
                    Open to weekends or evenings?
                  </span>
                </div>
              </label>
            </div>
          </div>
        </div>

        <LanguagesWithFluencySection
          value={
            (userSliceData?.languagesWithFluency ?? []) as LanguageItemV2[]
          }
          onChange={(next) =>
            dispatch(updateUser({ field: "languagesWithFluency", value: next }))
          }
        />

        {/* ── Pay & Preferences ── */}
        {/* <SectionDivider title="Pay & Preferences" /> */}

        {/* Avoid topics — same chip style as Step4 avoidTopics block */}
        {/* <div>
          <p className="tracking-normal text-sm">
            <span className="text-[#121217] font-medium">
              Industries / Topics not comfortable with
            </span>{" "}
            <span className="text-[#475467]">(optional)</span>
          </p>
          <p className="mt-[2px] text-[10px] tracking-normal text-[#475467] mb-2">
            This helps us avoid mismatched projects.
          </p>
          <AuthInput
            name="uncomfortableIndustriesTopics"
            placeholder="e.g. Alcohol, Gambling, Adult products..."
            value={userSliceData?.brandsDontWantToWorkWith}
            onChange={(e) =>
              dispatch(
                updateUser({
                  field: "brandsDontWantToWorkWith",
                  value: e.target.value,
                }),
              )
            }
          />
        </div>

        <AuthInput
          label="Last Drawn Salary"
          name="salary"
          placeholder="e.g. ₹50,000 / month"
          value={userSliceData?.salary}
          onChange={(e) =>
            dispatch(updateUser({ field: "salary", value: e.target.value }))
          }
        />

        <AuthInput
          label="Expected Pay per Video / Month"
          name="payPerVideo"
          placeholder="e.g. ₹5,000 per video"
          value={userSliceData?.payPerVideo}
          onChange={(e) =>
            dispatch(
              updateUser({ field: "payPerVideo", value: e.target.value }),
            )
          }
        /> */}

        {/* <AuthInput
          label="Languages Spoken"
          name="language"
          placeholder="e.g. English, Hindi, Tamil"
          value={userSliceData?.language}
          onChange={(e) =>
            dispatch(updateUser({ field: "language", value: e.target.value }))
          }
        /> */}

        {/* ── Wardrobe & Backgrounds ── */}
        <SectionDivider title="Wardrobe & Backgrounds" />

        <div>
          <p className="tracking-normal text-sm">
            <span className="text-[#121217] font-medium">Wardrobe Options</span>{" "}
            <span className="text-[#475467]">(optional)</span>
          </p>
          <p className="text-[#475467] text-[10px] tracking-normal mt-[2px] mb-3">
            Upload 3–4 images of outfits you can wear on camera.
          </p>
          <FileUploadFieldPlus
            accept={{ "image/png": [], "image/jpeg": [] }}
            useLiveUrl={true}
            defaultValue={userSliceData?.wardrobeStyles as FileDetails[]}
            onFilesUploaded={(value) =>
              dispatch(updateUser({ field: "wardrobeStyles", value }))
            }
          />
        </div>

        <div>
          <p className="tracking-normal text-sm">
            <span className="text-[#121217] font-medium">
              Background Options
            </span>{" "}
            <span className="text-[#475467]">(optional)</span>
          </p>
          <p className="text-[#475467] text-[10px] tracking-normal mt-[2px] mb-3">
            Upload 3–4 images of backgrounds you can film in front of.
          </p>
          <FileUploadFieldPlus
            accept={{ "image/png": [], "image/jpeg": [] }}
            useLiveUrl={true}
            defaultValue={userSliceData.availableBackgrounds as FileDetails[]}
            onFilesUploaded={(value) =>
              dispatch(updateUser({ field: "availableBackgrounds", value }))
            }
          />
        </div>
      </div>

      {/* ── Footer — exact same Go Back / Proceed pill button pattern ── */}
      <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#F2F4F7] shrink-0">
        <CustomButton
          variant="outline"
          border="1px solid #E4E4E4"
          fontSize="14px"
          fontWeight="600"
          color="#4D4D4D"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handlerCancel}
        >
          Cancel
        </CustomButton>

        <CustomButton
          backgroundColor="#FF6600"
          fontSize="14px"
          fontWeight="600"
          color="white"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handlerUpdateCreator}
        >
          Save Changes
        </CustomButton>
      </div>
    </section>
  );
};

export default EditProfessionalDetailsModal;
